import React, { useState } from 'react';
import { classNames } from 'primereact/utils';
import { useTranslation } from 'react-i18next';
import { Skeleton } from 'primereact/skeleton';
import { SessionFile } from 'types/SessionFile';
import { Session } from 'types/Session';
import { UploadUserFilesDialog } from 'components/session/yourNotes/UploadUserFilesDialog';
import { Dialog, DialogConfirm } from 'components/_new/Dialog';
import { usePanel } from 'contexts/panelContext';
import { Button } from 'components/_new/Button';
import { userFilesApi } from 'api/userFiles';
import { useMutation } from '@tanstack/react-query';
import { EmptyState } from 'utils/emptyState';
import { DividerList } from 'components/_new/Divider';
import { DispalyModule } from './DisplayModule';
import { NoteItem } from './NoteItem';

type NoteListProps = {
	title?: string;
	items: SessionFile[];
	onUpdate?: () => void;
	isLoading?: boolean;
	displaySessionNumberInfo?: boolean;
	dividedList?: boolean;
	defaultOpenInPreview?: boolean;
	currentSession?: Session;
};

export const NoteList = ({
	title,
	items,
	onUpdate,
	isLoading = false,
	displaySessionNumberInfo = false,
	dividedList = false,
	defaultOpenInPreview = false,
	currentSession,
}: NoteListProps) => {
	const { t } = useTranslation();

	const { currentApplicationData } = usePanel();

	// preview
	const [previewModalOpen, setPreviewModalOpen] = useState(false);
	const [selectedNote, setSelectedNote] = useState<SessionFile | undefined>(undefined);
	const canManageSelected =
		Number(selectedNote?.authorApplicationId ?? selectedNote?.authorApplication?.id) ===
		Number(currentApplicationData.id);

	// edit
	const [editSessionNoteModalOpen, setEditSessionNoteModalOpen] = useState(false);

	// remove
	const [deleteSessionNoteModalOpen, setDeleteSessionNodeModalOpen] = useState(false);
	const { mutateAsync: deleteUserFileMutateAsync } = useMutation((id: number) => userFilesApi.deleteUserFile(id), {
		onSuccess: (res: any) => {
			onUpdate?.();
		},
	});
	const handleDeleteUserFile = () => {
		if (selectedNote) {
			deleteUserFileMutateAsync(selectedNote.id);
		}
	};

	const list = items.map((data) => {
		const canManage =
			Number(data.authorApplicationId ?? data.authorApplication?.id) === Number(currentApplicationData.id);
		return (
			<NoteItem
				key={data.id}
				data={data}
				onPreviewClick={() => {
					setSelectedNote(data);
					setPreviewModalOpen(true);
				}}
				onDeleteClick={
					canManage
						? () => {
								setDeleteSessionNodeModalOpen(true);
								setSelectedNote(data);
						  }
						: undefined
				}
				onEditClick={
					canManage
						? () => {
								setEditSessionNoteModalOpen(true);
								setSelectedNote(data);
						  }
						: undefined
				}
				bordered={!dividedList}
				slim
				displaySessionNumberInfo={displaySessionNumberInfo}
				defaultOpenInPreview={defaultOpenInPreview}
			/>
		);
	});

	return (
		<>
			<Dialog
				title={selectedNote?.name || ''}
				size="lg"
				visible={previewModalOpen && Boolean(selectedNote)}
				onHide={() => {
					setPreviewModalOpen(false);
					setSelectedNote(undefined);
				}}
				headerRight={
					canManageSelected
						? () => (
								<Button
									label={t('actions.edit')}
									icon="pencil"
									variant="primary-outlined"
									variantSize="sm"
									onClick={() => {
										setSelectedNote(selectedNote);
										setEditSessionNoteModalOpen(true);
									}}
								/>
						  )
						: undefined
				}
			>
				{selectedNote && <DispalyModule userFileId={selectedNote.id} />}
			</Dialog>
			<UploadUserFilesDialog
				session={currentSession || selectedNote?.session || undefined}
				selectedUserfile={selectedNote}
				setSelectedUserfile={setSelectedNote}
				visible={editSessionNoteModalOpen && Boolean(selectedNote)}
				onHide={() => {
					setSelectedNote(undefined);
					setEditSessionNoteModalOpen(false);
				}}
				onFinish={() => {
					onUpdate?.();
				}}
			/>
			<DialogConfirm
				title={t('userPanel.userFiles.confirmDeleteTitle')}
				onConfirm={handleDeleteUserFile}
				onReject={() => {
					setSelectedNote(undefined);
				}}
				visible={deleteSessionNoteModalOpen && Boolean(selectedNote)}
				onHide={() => {
					setDeleteSessionNodeModalOpen(false);
					setSelectedNote(undefined);
				}}
			>
				{t('userPanel.userFiles.confirmDeleteDesc')}
			</DialogConfirm>
			<div className="flex flex-column gap-2">
				{title && (
					<div>
						<div className="text-black font-bold text-base">{title}</div>
					</div>
				)}
				<div className="flex flex-column gap-6">
					{isLoading && (
						<>
							{[1, 2, 3].map((i) => (
								<Skeleton key={i} width="100%" height="3.5rem" className="border-round-xl" />
							))}
						</>
					)}
					{!isLoading && items.length === 0 && <EmptyState description={t('userPanel.files.fileNotFound')} />}
					{!isLoading && items.length > 0 && (
						<div className="md:flex-1">
							{dividedList ? (
								<DividerList gap={0} className="md:flex-1">
									{list}
								</DividerList>
							) : (
								<ul className={classNames('flex flex-column m-0 p-0 gap-2')}>{list}</ul>
							)}
						</div>
					)}
				</div>
			</div>
		</>
	);
};
